import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AdminShell } from '@/components/layout/AdminShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { OverviewPage } from '@/features/OverviewPage';
import { ForbiddenPage } from '@/features/ForbiddenPage';
import { TenantsPage } from '@/features/tenants/TenantsPage';
import { UsersPage } from '@/features/users/UsersPage';
import { TenantDetailPage } from '@/features/tenants/TenantDetailPage';
import { ConversationsPage } from '@/features/messages/ConversationsPage';
import { ConversationDetailPage } from '@/features/messages/ConversationDetailPage';
import { PlansPage } from '@/features/plans/PlansPage';
import { VideoOverlayTemplatesPage } from '@/features/video-overlay-templates/VideoOverlayTemplatesPage';
import { TemplatesPage } from '@/features/templates/TemplatesPage';
import { TeamPage } from '@/features/team/TeamPage';
import { SecurityPage } from '@/features/security/SecurityPage';
import { useAuthStore } from '@/stores/auth-store';

const rootRoute = createRootRoute({
  component: Outlet,

  notFoundComponent: () => (
    <ForbiddenPage
      title="Page introuvable"
      description="Le lien que vous avez suivi ne correspond à aucune page du back-office."
    />
  ),
});

/**
 * Garde d'accès au back-office.
 *
 * Contrairement à `frontend/`, qui distingue routes publiques et routes authentifiées,
 * ICI TOUTE L'APPLICATION est l'espace admin : il n'y a pas de garde `requireAuth`
 * générique suivie d'une garde admin imbriquée, une seule garde couvre les deux niveaux
 * (session absente → connexion, session sans la permission → 403).
 *
 * À NOTER, comme côté `frontend/` : c'est du confort d'interface, pas la barrière de
 * sécurité réelle. Celle-ci vit côté API (`Permissions.PlatformAdmin`, appliquée deux fois
 * dans le pipeline CQRS et sur chaque endpoint).
 */
function requirePlatformAdmin({ location }: { location: { href: string } }) {
  const { accessToken, isBootstrapped, user } = useAuthStore.getState();

  if (isBootstrapped && accessToken === null) {
    throw redirect({ to: '/connexion', search: { redirect: location.href } });
  }

  if (isBootstrapped && user !== null && !user.permissions.includes('platform:admin')) {
    throw redirect({ to: '/interdit' });
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion',
  component: LoginPage,

  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === 'string' ? { redirect: search.redirect } : {},
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/interdit',
  component: () => (
    <ForbiddenPage
      title="Accès refusé"
      description="Votre compte ne dispose pas du rôle Platform Admin requis pour accéder au back-office Pixlide."
    />
  ),
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin',
  beforeLoad: requirePlatformAdmin,
  component: AdminShell,
});

const overviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: OverviewPage,
});

/**
 * Liste des utilisateurs, tous comptes clients confondus : c'est l'entrée « personnes » du
 * back-office, et l'entrée du menu latéral.
 */
const usersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/utilisateurs',
  component: UsersPage,
});

/**
 * Liste des comptes clients. Elle n'est plus dans le menu latéral — le back-office s'ouvre
 * désormais sur les utilisateurs — mais reste atteignable depuis l'écran « Utilisateurs » et
 * depuis le retour de la fiche d'un tenant : les actions de cycle de vie d'un compte client
 * (suspendre, réactiver, résilier) ne vivent nulle part ailleurs, et un client sans aucun
 * utilisateur n'apparaîtrait dans aucune autre liste.
 */
const tenantsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/tenants',
  component: TenantsPage,
});

const tenantDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/tenants/$tenantId',
  component: TenantDetailPage,
});

const messagesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/messages',
  component: ConversationsPage,
});

const messageDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/messages/$tenantId',
  component: ConversationDetailPage,
});

const plansRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/plans',
  component: PlansPage,
});

const videoOverlayTemplatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/templates-habillage',
  component: VideoOverlayTemplatesPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/templates',
  component: TemplatesPage,
});

const teamRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/equipe',
  component: TeamPage,
});

const securityRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/securite',
  component: SecurityPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forbiddenRoute,
  adminRoute.addChildren([
    overviewRoute,
    usersRoute,
    tenantsRoute,
    tenantDetailRoute,
    messagesRoute,
    messageDetailRoute,
    plansRoute,
    videoOverlayTemplatesRoute,
    templatesRoute,
    teamRoute,
    securityRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
